import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import type { UserRole } from "../../../lib/api";
import { consoleApi, type ConsoleAccountDetail } from "../../../lib/admin-console-api";
import { useAuth } from "../../../lib/auth";
import { useAppPreferences } from "../../../lib/preferences";
import { useConfirm } from "../../../components/ConfirmDialog";
import { ActivityTimeline, NotesPanel } from "../activity";
import { errorMessage, Facts, LoadState, PageHeader, Section, StatusChip, useFormatters, useLoad, useNotices, useReasonPrompt, type Translate } from "../ui";

const ROLE_FILTERS: Array<{ id: "" | UserRole; label: string }> = [
  { id: "", label: "All" },
  { id: "customer", label: "Players" },
  { id: "provider_owner", label: "Venue owners" },
  { id: "provider_staff", label: "Venue staff" },
  { id: "support", label: "Support" },
  { id: "admin", label: "Admins" },
];

export function roleName(role: UserRole, t: Translate) {
  const labels: Record<UserRole, string> = {
    admin: "Admin",
    support: "Support",
    provider_owner: "Venue owner",
    provider_staff: "Venue staff",
    customer: "Player",
  };
  return t(labels[role] ?? role);
}

export function AccountsPage() {
  const { t } = useAppPreferences();
  const { dateOnly } = useFormatters();
  const [params, setParams] = useSearchParams();
  const role = (params.get("role") ?? "") as "" | UserRole;
  const query = params.get("q") ?? "";
  const [draft, setDraft] = useState(query);
  const accounts = useLoad(() => consoleApi.listAccounts({ q: query, role, limit: 100 }).then((result) => result.accounts), [query, role]);
  const update = (next: Record<string, string>) => {
    const merged = { role, q: query, ...next };
    setParams(Object.fromEntries(Object.entries(merged).filter(([, value]) => value)));
  };

  return (
    <div className="console-page">
      <PageHeader description={t("Players, venue owners and staff, and the gaamed team.")} kicker={t("Records")} title={t("Accounts")} />
      <form
        className="console-toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          update({ q: draft.trim() });
        }}
        role="search"
      >
        <input
          aria-label={t("Find accounts")}
          className="ops-input console-filter-input"
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t("Email, name or phone")}
          value={draft}
        />
        <button className="ops-button" data-size="sm" data-variant="secondary" type="submit">{t("Search")}</button>
        <div className="console-pills">
          {ROLE_FILTERS.map((item) => (
            <button aria-pressed={role === item.id} className="ops-filter-pill" data-active={role === item.id} key={item.id || "all"} onClick={() => update({ role: item.id })} type="button">
              {t(item.label)}
            </button>
          ))}
        </div>
      </form>
      <LoadState empty={t("No accounts match.")} error={accounts.error} isEmpty={(accounts.data ?? []).length === 0} loading={accounts.loading} onRetry={() => void accounts.reload()}>
        <div className="ops-surface console-table-wrap">
          <table className="console-table">
            <thead>
              <tr>
                <th scope="col">{t("Account")}</th>
                <th scope="col">{t("Type")}</th>
                <th scope="col">{t("Status")}</th>
                <th scope="col">{t("Joined")}</th>
              </tr>
            </thead>
            <tbody>
              {(accounts.data ?? []).map((account) => (
                <tr key={account.id}>
                  <td>
                    <Link className="console-link" to={`/admin/accounts/${account.id}`}>{account.display_name ?? account.email}</Link>
                    <span className="console-muted console-truncate block">{account.email}{account.phone ? ` · ${account.phone}` : ""}</span>
                  </td>
                  <td>
                    {roleName(account.role, t)}
                    {account.provider_name ? <span className="console-muted block">{account.provider_name}</span> : null}
                  </td>
                  <td><AccountState account={account} /></td>
                  <td>{dateOnly(account.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </LoadState>
    </div>
  );
}

function AccountState({ account }: { account: { is_active: boolean; email_verified: boolean; setup_pending: boolean } }) {
  const { t } = useAppPreferences();
  if (!account.is_active) return <StatusChip status="inactive" />;
  if (account.setup_pending) return <span className="ops-chip" data-tone="warning">{t("Setup pending")}</span>;
  if (!account.email_verified) return <span className="ops-chip" data-tone="warning">{t("Email not verified")}</span>;
  return <StatusChip status="active" />;
}

/** Plain-language answer to "why can't this person sign in?". */
export function signInDiagnosis(account: ConsoleAccountDetail, t: Translate) {
  if (!account.is_active) return t("The account is deactivated, so sign-in is refused.");
  if (account.setup_pending) return t("They haven't set a password yet. They need the setup link.");
  if (!account.email_verified && account.role === "customer") return t("Their email isn't verified. Sign-in asks for the verification code first.");
  const blocked = account.memberships.find((membership) => membership.provider_status !== "approved");
  if (blocked) return t("They can sign in, but the {provider} workspace is {status}.", { provider: blocked.provider_name, status: t(blocked.provider_status) });
  return t("Nothing blocks sign-in. If they still can't get in, send a password reset link.");
}

export function AccountRecordPage() {
  const { accountId = "" } = useParams();
  const { t } = useAppPreferences();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { dateOnly, dateTime } = useFormatters();
  const { banners, setError, setNotice } = useNotices();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const { ask, dialog: reasonDialog } = useReasonPrompt();
  const account = useLoad(() => consoleApi.getAccount(accountId), [accountId]);
  const [busy, setBusy] = useState(false);
  const detail = account.data;
  const isSelf = detail?.id === user?.id;

  const act = async (work: () => ReturnType<typeof consoleApi.accountAction>, failure: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await work();
      account.setData(result.account);
      setNotice(result.email_sent === false ? t("{message} Email delivery failed; try again later.", { message: result.message }) : result.message);
    } catch (error) {
      setError(errorMessage(error, failure));
    } finally {
      setBusy(false);
    }
  };

  const signOutEverywhere = async (target: ConsoleAccountDetail) => {
    const ok = await confirm({
      title: t("Sign {name} out of every device?", { name: target.display_name ?? target.email }),
      description: t("Use this for a lost phone or a shared device. Their password doesn't change."),
      confirmButtonLabel: t("Sign out everywhere"),
      cancelButtonLabel: t("Cancel"),
    });
    if (ok) await act(() => consoleApi.accountAction(target.id, "sign-out"), t("They were not signed out."));
  };

  const toggleActive = async (target: ConsoleAccountDetail) => {
    const activating = !target.is_active;
    const reason = await ask({
      title: activating ? t("Reactivate this account?") : t("Deactivate this account?"),
      description: activating
        ? t("They can sign in again with their existing password.")
        : t("They are signed out everywhere and can't sign in until an admin reactivates them."),
      confirmLabel: activating ? t("Reactivate") : t("Deactivate"),
      fieldLabel: t("Reason (kept in the audit log)"),
      minLength: 5,
      tone: activating ? "primary" : "danger",
    });
    if (reason) await act(() => consoleApi.setAccountActive(target.id, activating, reason), t("The account was not updated."));
  };

  return (
    <div className="console-page">
      {confirmDialog}
      {reasonDialog}
      <LoadState error={account.error} isEmpty={!detail} loading={account.loading} onRetry={() => void account.reload()}>
        {detail ? (
          <>
            <PageHeader
              description={<><AccountState account={detail} /> <span className="console-muted">{roleName(detail.role, t)}</span></>}
              kicker={<Link className="console-link" to="/admin/accounts">{t("Accounts")}</Link>}
              title={detail.display_name ?? detail.email}
            />
            {banners}
            <div className="console-record-grid">
              <div className="console-record-main">
                <Section title={t("Sign-in")}>
                  <p className="console-diagnosis">{signInDiagnosis(detail, t)}</p>
                  <Facts
                    items={[
                      [t("Email"), detail.email],
                      [t("Phone"), detail.phone],
                      [t("Email verified"), detail.email_verified ? t("Yes") : t("No")],
                      [t("Signed-in devices"), String(detail.active_session_count)],
                      [t("Last sign-in"), detail.last_sign_in_at ? dateTime(detail.last_sign_in_at) : t("Never")],
                      [t("Joined"), dateOnly(detail.created_at)],
                    ]}
                  />
                  {detail.is_platform_staff ? (
                    <p className="console-muted">
                      {t("This is a gaamed team account.")} {isAdmin ? <Link className="console-link" to="/admin/team">{t("Manage it on the Team page.")}</Link> : null}
                    </p>
                  ) : isSelf ? null : (
                    <div className="console-button-row">
                      {!detail.email_verified && detail.is_active ? (
                        <button className="ops-button" data-size="sm" data-variant="secondary" disabled={busy} onClick={() => void act(() => consoleApi.accountAction(detail.id, "resend-verification"), t("The code was not sent."))} type="button">
                          {t("Resend verification code")}
                        </button>
                      ) : null}
                      {detail.is_active && !detail.setup_pending ? (
                        <button className="ops-button" data-size="sm" data-variant="secondary" disabled={busy} onClick={() => void act(() => consoleApi.accountAction(detail.id, "password-reset"), t("The reset link was not sent."))} type="button">
                          {t("Send password reset link")}
                        </button>
                      ) : null}
                      {detail.active_session_count > 0 ? (
                        <button className="ops-button" data-size="sm" data-variant="secondary" disabled={busy} onClick={() => void signOutEverywhere(detail)} type="button">
                          {t("Sign out everywhere")}
                        </button>
                      ) : null}
                      {isAdmin && !(detail.role === "provider_owner" && detail.is_active) ? (
                        <button className="ops-button" data-size="sm" data-variant={detail.is_active ? "danger" : "primary"} disabled={busy} onClick={() => void toggleActive(detail)} type="button">
                          {detail.is_active ? t("Deactivate") : t("Reactivate")}
                        </button>
                      ) : null}
                    </div>
                  )}
                  {detail.role === "provider_owner" && detail.is_active && isAdmin ? (
                    <p className="console-muted">{t("To block a venue owner, suspend their provider instead.")}</p>
                  ) : null}
                </Section>

                {detail.memberships.length > 0 ? (
                  <Section title={t("Venue business")}>
                    <ul className="console-list">
                      {detail.memberships.map((membership) => (
                        <li key={membership.provider_id}>
                          <Link className="console-list-row" to={`/admin/providers/${membership.provider_id}`}>
                            <span className="min-w-0 flex-1">
                              <span className="console-list-title">{membership.provider_name}</span>
                              <span className="console-muted">
                                {t({ owner: "Owner", manager: "Manager", front_desk: "Front desk" }[membership.membership_role])}
                                {membership.venue_ids ? ` · ${t("{count} venues", { count: membership.venue_ids.length })}` : ` · ${t("All venues")}`}
                              </span>
                            </span>
                            <StatusChip status={membership.provider_status} />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </Section>
                ) : null}

                {detail.deletion_requests.length > 0 ? (
                  <Section actions={<Link className="console-link" to="/admin/deletion-requests">{t("Open deletion queue")}</Link>} title={t("Account deletion")}>
                    <ul className="console-list">
                      {detail.deletion_requests.map((request) => (
                        <li className="console-list-row" key={request.id}>
                          <span className="min-w-0 flex-1">
                            <span className="console-list-title">{t("Requested {date}", { date: dateTime(request.requested_at) })}</span>
                            {request.reason ? <span className="console-muted">{request.reason}</span> : null}
                            {request.admin_note ? <span className="console-muted">{t("Admin note: {note}", { note: request.admin_note })}</span> : null}
                          </span>
                          <StatusChip status={request.status} />
                        </li>
                      ))}
                    </ul>
                  </Section>
                ) : null}

                {detail.role === "customer" ? (
                  <Section description={t("Reports filed: {filed} · reports about them: {against}", { filed: detail.reports_filed, against: detail.reports_against })} title={t("Reviews")}>
                    {detail.reviews.length === 0 ? (
                      <p className="console-muted">{t("No reviews written.")}</p>
                    ) : (
                      <ul className="console-list">
                        {detail.reviews.map((review) => (
                          <li className="console-review" key={review.id}>
                            <div className="min-w-0 flex-1">
                              <p className="console-list-title">
                                {"★".repeat(review.stars)}{"☆".repeat(5 - review.stars)} · <Link className="console-link" to={`/admin/venues/${review.venue_id}`}>{review.venue_name}</Link>
                              </p>
                              <p className="console-quote">{review.comment || t("No comment")}</p>
                              {review.review_note ? <p className="console-muted">{t("Moderator note: {note}", { note: review.review_note })}</p> : null}
                            </div>
                            <StatusChip status={review.status} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </Section>
                ) : null}

                <ActivityTimeline filters={{ entity_type: "user", entity_id: detail.id }} />
              </div>
              <div className="console-record-side">
                {detail.role === "customer" ? (
                  <Section title={t("Venue issues")}>
                    <p className="console-muted">
                      {t("Bookings, sessions, food orders and payments are handled by the venue. Point the player to the venue's front desk or phone.")}
                    </p>
                  </Section>
                ) : null}
                <NotesPanel entityId={detail.id} entityType="user" />
              </div>
            </div>
          </>
        ) : null}
      </LoadState>
    </div>
  );
}
